import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Stack,
  Text,
  Group,
  useMantineColorScheme,
  UnstyledButton,
  Avatar,
  Divider,
  Image,
  Tooltip,
  Menu,
  Collapse,
} from '@mantine/core';
import { profileService } from '../services/profileService';
import { authService } from '../services/authService';
import enarmLogo from '../assets/enarm_logo.png';
import {
  IconDashboard,
  IconClipboardList,
  IconChartBar,
  IconTrophy,
  IconPlus,
  IconSettings,
  IconChevronDown,
  IconUser,
  IconLogout,
  IconCreditCard,
  IconCash,
  IconDatabase,
  IconBell,
  IconMessageReport,
  IconLayoutSidebarLeftCollapse,
  IconLayoutSidebarRightCollapse,
  IconStethoscope,
  IconCards,
  IconSparkles,
  IconArrowRight,
  IconVideo,
  IconFolder,
  IconBriefcase,
  IconUsers,
  IconFileText,
} from '@tabler/icons-react';
import { useSidebar } from '../contexts/SidebarContext';

// Estructura del menú de administrador.
// Items "top" siempre visibles; el resto vive dentro de grupos colapsables.
interface NavItem {
  id: string;
  label: string;
  icon: React.ComponentType<any>;
  path: string;
  disabled?: boolean;
}

interface NavGroup {
  id: string;
  label: string;
  icon: React.ComponentType<any>;
  items: NavItem[];
}

const ADMIN_TOP_ITEMS: NavItem[] = [
  { id: 'dashboard', label: 'Dashboard', icon: IconDashboard, path: '/admin/dashboard' },
  { id: 'statistics', label: 'Estadísticas', icon: IconChartBar, path: '/admin/statistics' },
];

const ADMIN_GROUPS: NavGroup[] = [
  {
    id: 'contenido',
    label: 'Contenido',
    icon: IconFolder,
    items: [
      { id: 'question-database', label: 'Reactivos', icon: IconDatabase, path: '/admin/question-database' },
      { id: 'clinical-cases', label: 'Casos Clínicos', icon: IconStethoscope, path: '/admin/clinical-cases' },
      { id: 'flashcards', label: 'Anki', icon: IconCards, path: '/admin/flashcards' },
      { id: 'videos', label: 'Videos', icon: IconVideo, path: '/admin/videos' },
      { id: 'materiales', label: 'Materiales', icon: IconFileText, path: '/admin/materiales' },
      { id: 'referencia-enarm', label: 'Referencia ENARM', icon: IconTrophy, path: '/admin/referencia-enarm' },
    ],
  },
  {
    id: 'negocio',
    label: 'Negocio',
    icon: IconBriefcase,
    items: [
      { id: 'subscriptions', label: 'Suscripciones', icon: IconCreditCard, path: '/admin/subscriptions' },
      { id: 'finanzas', label: 'Finanzas', icon: IconCash, path: '/admin/finanzas' },
    ],
  },
  {
    id: 'usuarios',
    label: 'Usuarios',
    icon: IconUsers,
    items: [
      { id: 'users', label: 'Usuarios', icon: IconUser, path: '/admin/usuarios' },
      { id: 'quejas', label: 'Quejas y Sugerencias', icon: IconMessageReport, path: '/admin/quejas' },
      { id: 'notifications', label: 'Notificaciones', icon: IconBell, path: '/admin/notificaciones' },
    ],
  },
];

// Items aplanados para el modo colapsado (solo iconos): top + todos los hijos de los grupos.
const ADMIN_FLAT_ITEMS: NavItem[] = [
  ...ADMIN_TOP_ITEMS,
  ...ADMIN_GROUPS.flatMap((g) => g.items),
];

interface SidebarProps {
  user: {
    username: string;
    email: string;
    roles?: string[];
    nombre?: string;
    apellidos?: string;
    avatar?: string;
  };
  onLogout: () => void;
  onCollapseChange?: (collapsed: boolean) => void;
  initialCollapsed?: boolean;
  isMobile?: boolean;
  mobileOpen?: boolean;
  onMobileClose?: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ user, onLogout, onCollapseChange, initialCollapsed = false, isMobile = false, mobileOpen = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { colorScheme } = useMantineColorScheme();

  const { collapsed: contextCollapsed, setCollapsed: setContextCollapsed } = useSidebar();
  const [isCollapsed, setIsCollapsed] = useState(initialCollapsed);
  const [esUsuarioGratuito, setEsUsuarioGratuito] = useState<boolean | null>(null);

  // Determinar si el usuario es admin (necesario arriba para el useEffect de plan).
  const isAdmin = user?.roles?.includes('ROLE_ADMIN') || user?.roles?.includes('ADMIN');

  // Verificar si el usuario es gratuito (solo estudiantes).
  useEffect(() => {
    if (isAdmin) return;
    const verificarPlan = async () => {
      try {
        const { userSubscriptionService } = await import('../services/userSubscriptionService');
        const statusData = await userSubscriptionService.getSubscriptionStatus();
        const esPlanGratuito = statusData?.isFreeplan === true || !statusData?.subscriptionId;
        setEsUsuarioGratuito(esPlanGratuito);
      } catch (error) {
        console.error('Error verificando plan:', error);
        setEsUsuarioGratuito(false);
      }
    };
    verificarPlan();
  }, [isAdmin]);


  const handleCollapseChange = (collapsed: boolean) => {
    setIsCollapsed(collapsed);
    setContextCollapsed(collapsed);
    onCollapseChange?.(collapsed);
  };

  // Notificar el estado inicial
  useEffect(() => {
    onCollapseChange?.(initialCollapsed);
  }, [initialCollapsed, onCollapseChange]);

  // Actualizar estado cuando initialCollapsed cambie (en móvil/tablet vertical: nunca colapsado)
  useEffect(() => {
    setIsCollapsed(isMobile ? false : initialCollapsed);
  }, [initialCollapsed, isMobile]);

  // Sincronizar con el contexto cuando cambie externamente (en móvil/tablet vertical: nunca colapsado)
  useEffect(() => {
    setIsCollapsed(isMobile ? false : contextCollapsed);
    onCollapseChange?.(contextCollapsed);
  }, [contextCollapsed, onCollapseChange, isMobile]);

  // Grupos colapsables del admin: qué grupo está abierto.
  // Se auto-abre el grupo que contiene la ruta activa.
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>(() => {
    const active = ADMIN_GROUPS.find((g) => g.items.some((i) => i.path === window.location.pathname));
    return active ? { [active.id]: true } : { contenido: true };
  });

  const toggleGroup = (id: string) => {
    setOpenGroups((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  // Menús para estudiante
  const studentMenuItems = [
    {
      id: 'dashboard',
      label: 'Dashboard',
      icon: IconDashboard,
      path: '/estudiante/dashboard',
      disabled: false,
    },
    {
      id: 'simulador',
      label: 'Simulador',
      icon: IconClipboardList,
      path: '/estudiante/simulador',
      disabled: false,
    },
    {
      id: 'estadisticas',
      label: 'Estadísticas',
      icon: IconChartBar,
      path: '/estudiante/estadisticas',
      disabled: false,
    },
    {
      id: 'flashcards',
      label: 'Anki',
      icon: IconCards,
      path: '/estudiante/flashcards',
      disabled: false,
    },
    {
      id: 'videos',
      label: 'Videos',
      icon: IconVideo,
      path: '/estudiante/videos',
      disabled: false,
    },
    {
      id: 'materiales',
      label: 'Materiales',
      icon: IconFileText,
      path: '/estudiante/materiales',
      disabled: false,
    },
  ];

  // Menús de comunidad para estudiantes
  // Nota: "Notificaciones" se accede desde el botón de la barra superior
  // (NotificationDropdown → "Ver todas"), por eso no está en el sidebar.
  const miPerfilId = authService.getCurrentUserFromStorage()?.id;
  const studentCommunityItems = [
    {
      id: 'profile',
      label: 'Mi perfil',
      icon: IconUser,
      path: miPerfilId ? `/perfil/${miPerfilId}` : '/settings',
      disabled: false,
    },
    {
      id: 'rankings',
      label: 'Rankings',
      icon: IconTrophy,
      path: '/estudiante/rankings',
      disabled: false,
    },
  ];

  // Agregar "Crear Preguntas" si el usuario tiene permisos de creator
  if (user?.roles?.includes('ROLE_CREATOR') || user?.roles?.includes('CREATOR')) {
    studentMenuItems.push({
      id: 'preguntas',
      label: 'Crear Preguntas',
      icon: IconPlus,
      path: '/estudiante/preguntas',
      disabled: false,
    });
  }

  // Menú de estudiante (el admin usa ADMIN_TOP_ITEMS + ADMIN_GROUPS).
  const menuItems = studentMenuItems;
  const communityItems = studentCommunityItems;

  const isActive = (path: string) => location.pathname === path;

  // Auto-abrir el grupo del admin que contiene la ruta activa al navegar.
  useEffect(() => {
    const active = ADMIN_GROUPS.find((g) => g.items.some((i) => i.path === location.pathname));
    if (active) {
      setOpenGroups((prev) => (prev[active.id] ? prev : { ...prev, [active.id]: true }));
    }
  }, [location.pathname]);

  const MenuItem: React.FC<{
    item: {
      id: string;
      label: string;
      icon: React.ComponentType<any>;
      path: string;
      disabled?: boolean;
    };
  }> = ({ item }) => {
    const IconComponent = item.icon;
    const active = isActive(item.path);
    const isDisabled = item.disabled || false;

    if (isCollapsed) {
      return (
        <Tooltip label={isDisabled ? `${item.label} - Próximamente` : item.label} position="right" withArrow>
          <UnstyledButton
            onClick={() => !isDisabled && navigate(item.path)}
            style={{
              width: '40px',
              height: '40px',
              borderRadius: '25px',
              backgroundColor: active
                ? colorScheme === 'dark'
                  ? 'rgba(255, 255, 255, 0.1)'
                  : 'rgba(255, 255, 255, 0.6)'
                : 'transparent',
              border: active
                ? `1px solid ${colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(255, 255, 255, 0.8)'}`
                : '1px solid transparent',
              boxShadow: 'none',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              opacity: isDisabled ? 0.4 : 1,
              cursor: isDisabled ? 'not-allowed' : 'pointer',
            }}
            onMouseEnter={(e) => {
              if (!active && !isDisabled) {
                e.currentTarget.style.backgroundColor = colorScheme === 'dark'
                  ? 'rgba(55, 65, 81, 0.4)'
                  : 'rgba(255, 255, 255, 0.6)';
                e.currentTarget.style.transform = 'translateY(-1px) scale(1.02)';
              }
            }}
            onMouseLeave={(e) => {
              if (!active && !isDisabled) {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.transform = 'translateY(0px) scale(1)';
              }
            }}
          >
            <IconComponent
              size={18}
              style={{
                color: isDisabled
                  ? colorScheme === 'dark'
                    ? '#4b5563'
                    : '#9ca3af'
                  : active
                  ? colorScheme === 'dark'
                    ? '#e2e8f0'
                    : '#1e293b'
                  : colorScheme === 'dark'
                  ? '#94a3b8'
                  : '#64748b',
              }}
            />
          </UnstyledButton>
        </Tooltip>
      );
    }

    return (
      <UnstyledButton
        onClick={() => !isDisabled && navigate(item.path)}
        style={{
          width: '100%',
          padding: '8px 12px',
          borderRadius: '25px',
          backgroundColor: active
            ? colorScheme === 'dark'
              ? 'rgba(255, 255, 255, 0.1)'
              : 'rgba(255, 255, 255, 0.6)'
            : 'transparent',
          border: active
            ? `1px solid ${colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(255, 255, 255, 0.8)'}`
            : '1px solid transparent',
          boxShadow: 'none',
          transition: 'all 0.2s ease',
          opacity: isDisabled ? 0.4 : 1,
          cursor: isDisabled ? 'not-allowed' : 'pointer',
        }}
        onMouseEnter={(e) => {
          if (!active && !isDisabled) {
            e.currentTarget.style.backgroundColor = colorScheme === 'dark'
              ? 'rgba(55, 65, 81, 0.4)'
              : 'rgba(255, 255, 255, 0.6)';
            e.currentTarget.style.transform = 'translateY(-1px)';
          }
        }}
        onMouseLeave={(e) => {
          if (!active && !isDisabled) {
            e.currentTarget.style.backgroundColor = 'transparent';
            e.currentTarget.style.transform = 'translateY(0px)';
          }
        }}
      >
        <Group gap="xs" style={{ position: 'relative', width: '100%' }}>
          <IconComponent
            size={18}
            style={{
              color: isDisabled
                ? colorScheme === 'dark'
                  ? '#4b5563'
                  : '#9ca3af'
                : active
                ? colorScheme === 'dark'
                  ? '#e2e8f0'
                  : '#1e293b'
                : colorScheme === 'dark'
                ? '#94a3b8'
                : '#64748b',
            }}
          />
          <Text
            size="xs"
            fw={active ? 600 : 400}
            style={{
              color: isDisabled
                ? colorScheme === 'dark'
                  ? '#4b5563'
                  : '#9ca3af'
                : active
                ? colorScheme === 'dark'
                  ? '#e2e8f0'
                  : '#1e293b'
                : colorScheme === 'dark'
                ? '#94a3b8'
                : '#64748b',
              fontFamily: 'Inter, sans-serif',
              opacity: isCollapsed ? 0 : 1,
              transition: 'opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              transitionDelay: isCollapsed ? '0s' : '0.1s',
            }}
          >
            {item.label}
          </Text>
          {isDisabled && !isCollapsed && (
            <Text
              size="9px"
              style={{
                color: colorScheme === 'dark' ? '#6b7280' : '#9ca3af',
                fontFamily: 'Inter, sans-serif',
                fontStyle: 'italic',
                marginLeft: 'auto',
                opacity: isCollapsed ? 0 : 1,
                transition: 'opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                transitionDelay: isCollapsed ? '0s' : '0.1s',
              }}
            >
              Próximamente
            </Text>
          )}
        </Group>
      </UnstyledButton>
    );
  };

  // Grupo colapsable (solo modo expandido): cabecera con chevron + hijos en Collapse.
  const GroupSection: React.FC<{ group: NavGroup }> = ({ group }) => {
    const IconComponent = group.icon;
    const open = openGroups[group.id] ?? false;
    const hasActive = group.items.some((i) => isActive(i.path));
    const headerColor = hasActive
      ? colorScheme === 'dark'
        ? '#e2e8f0'
        : '#1e293b'
      : colorScheme === 'dark'
      ? '#94a3b8'
      : '#64748b';

    return (
      <Box>
        <UnstyledButton
          onClick={() => toggleGroup(group.id)}
          style={{
            width: '100%',
            padding: '8px 12px',
            borderRadius: '25px',
            backgroundColor: 'transparent',
            border: '1px solid transparent',
            boxShadow: 'none',
            transition: 'all 0.2s ease',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = colorScheme === 'dark'
              ? 'rgba(55, 65, 81, 0.4)'
              : 'rgba(255, 255, 255, 0.6)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
          }}
        >
          <Group gap="xs" style={{ width: '100%', flexWrap: 'nowrap' }}>
            <IconComponent size={18} style={{ color: headerColor, flexShrink: 0 }} />
            <Text
              size="xs"
              fw={hasActive ? 600 : 500}
              style={{
                color: headerColor,
                fontFamily: 'Inter, sans-serif',
              }}
            >
              {group.label}
            </Text>
            <IconChevronDown
              size={14}
              style={{
                marginLeft: 'auto',
                color: colorScheme === 'dark' ? '#94a3b8' : '#64748b',
                transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s ease',
                flexShrink: 0,
              }}
            />
          </Group>
        </UnstyledButton>

        <Collapse in={open}>
          <Stack gap={4} style={{ paddingLeft: '12px', marginTop: '4px' }}>
            {group.items.map((item) => (
              <MenuItem key={item.id} item={item} />
            ))}
          </Stack>
        </Collapse>
      </Box>
    );
  };

  return (
    <Box
      style={{
        width: isMobile ? (isAdmin ? '260px' : '220px') : (isCollapsed ? '60px' : (isAdmin ? '260px' : '220px')),
        height: '100vh',
        maxHeight: '100vh',
        // En móvil el sidebar es overlay: fondo sólido para que no se transparente el contenido.
        // En escritorio mantiene la translucidez sobre el degradado del fondo.
        backgroundColor: isMobile
          ? (colorScheme === 'dark' ? '#1e293b' : '#f7f3ee')
          : (colorScheme === 'dark'
            ? 'rgba(30, 41, 59, 0.7)'
            : 'rgba(247, 243, 238, 0.8)'),
        borderRight: `1px solid ${
          colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(221, 216, 209, 0.8)'
        }`,
        padding: (isCollapsed && !isMobile) ? '16px 12px' : '16px 16px',
        display: 'flex',
        flexDirection: 'column',
        position: 'fixed',
        left: 0,
        top: 0,
        zIndex: isMobile ? 1100 : 900,
        transform: isMobile ? (mobileOpen ? 'translateX(0)' : 'translateX(-100%)') : 'none',
        transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
        boxShadow: isMobile && mobileOpen ? '4px 0 24px rgba(0, 0, 0, 0.25)' : 'none',
        overflow: 'hidden',
      }}
    >
      {/* Botón de colapso fijo en esquina superior izquierda */}
      <Box
        onClick={() => handleCollapseChange(!isCollapsed)}
        style={{
          position: 'absolute',
          top: '12px',
          left: '12px',
          color: colorScheme === 'dark' ? '#94a3b8' : '#64748b',
          cursor: 'pointer',
          padding: '6px',
          borderRadius: '6px',
          backgroundColor: 'transparent',
          transition: 'all 0.2s ease',
          flexShrink: 0,
          width: '32px',
          height: '32px',
          display: isMobile ? 'none' : 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10,
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = colorScheme === 'dark'
            ? 'rgba(255, 255, 255, 0.1)'
            : 'rgba(0, 0, 0, 0.05)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = 'transparent';
        }}
      >
        {isCollapsed ? (
          <IconLayoutSidebarRightCollapse size={20} />
        ) : (
          <IconLayoutSidebarLeftCollapse size={20} />
        )}
      </Box>

      {/* Header con altura fija - logo visible solo cuando expandido */}
      <Box 
        mb="xl"
        style={{ 
          height: '110px', // Altura fija para evitar movimiento de iconos
          display: 'flex', 
          alignItems: 'center',
          justifyContent: 'center',
          paddingTop: '40px', // Espacio para el botón de colapso
          position: 'relative',
        }}
      >
        <Box
          onClick={() => navigate('/')}
          style={{
            width: '60px',
            height: '60px',
            borderRadius: '12px',
            overflow: 'hidden',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            opacity: isCollapsed ? 0 : 1,
            transform: isCollapsed ? 'scale(0.8)' : 'scale(1)',
            transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
            flexShrink: 0,
            pointerEvents: isCollapsed ? 'none' : 'auto',
            cursor: 'pointer',
          }}
          onMouseEnter={(e) => {
            if (!isCollapsed) {
              e.currentTarget.style.transform = 'scale(1.05)';
              e.currentTarget.style.backgroundColor = colorScheme === 'dark'
                ? 'rgba(255, 255, 255, 0.05)'
                : 'rgba(0, 0, 0, 0.03)';
            }
          }}
          onMouseLeave={(e) => {
            if (!isCollapsed) {
              e.currentTarget.style.transform = 'scale(1)';
              e.currentTarget.style.backgroundColor = 'transparent';
            }
          }}
        >
          <Image
            src={enarmLogo}
            alt="ENARM360"
            style={{
              width: '56px',
              height: '56px',
              objectFit: 'contain',
              transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
            }}
          />
        </Box>
      </Box>


      {/* Main Navigation */}
      <Stack 
        gap="xs" 
        style={{ 
          flex: 1, 
          minHeight: 0, 
          overflowY: 'auto', 
          overflowX: 'hidden', 
          alignItems: isCollapsed ? 'center' : 'stretch', 
          paddingRight: isCollapsed ? 0 : '8px',
        }}
        className="sidebar-scroll"
      >
        {isAdmin ? (
          isCollapsed ? (
            // Modo colapsado (60px): acordeones no caben → todo aplanado a iconos con tooltip.
            ADMIN_FLAT_ITEMS.map((item) => (
              <MenuItem key={item.id} item={item} />
            ))
          ) : (
            // Modo expandido: items "top" sueltos + grupos colapsables.
            <>
              {ADMIN_TOP_ITEMS.map((item) => (
                <MenuItem key={item.id} item={item} />
              ))}

              <Divider
                my="sm"
                color={colorScheme === 'dark' ? 'rgba(55, 65, 81, 0.3)' : 'rgba(226, 232, 240, 0.8)'}
              />

              {ADMIN_GROUPS.map((group) => (
                <GroupSection key={group.id} group={group} />
              ))}
            </>
          )
        ) : (
          <>
            {menuItems.map((item) => (
              <MenuItem key={item.id} item={item} />
            ))}

            <Divider
              my="md"
              color={colorScheme === 'dark' ? 'rgba(55, 65, 81, 0.3)' : 'rgba(226, 232, 240, 0.8)'}
            />

            {communityItems.map((item) => (
              <MenuItem key={item.id} item={item} />
            ))}
          </>
        )}

        {/* Premium Upgrade Card - solo para usuarios gratuitos no admin */}
        {!isAdmin && esUsuarioGratuito && !isCollapsed && (
          <Box
            onClick={() => navigate('/mejorarplan')}
            style={{
              marginTop: '8px',
              padding: '8px 10px',
              borderRadius: '12px',
              background: 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 50%, #d97706 100%)',
              cursor: 'pointer',
              position: 'relative',
              overflow: 'hidden',
              border: '1.5px solid rgba(251, 191, 36, 0.3)',
              boxShadow: `
                0 2px 8px rgba(251, 191, 36, 0.3),
                inset 0 1px 0 rgba(255, 255, 255, 0.3)
              `,
              transition: 'all 0.2s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-1px)';
              e.currentTarget.style.boxShadow = `
                0 4px 12px rgba(251, 191, 36, 0.4),
                inset 0 1px 0 rgba(255, 255, 255, 0.4)
              `;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = `
                0 2px 8px rgba(251, 191, 36, 0.3),
                inset 0 1px 0 rgba(255, 255, 255, 0.3)
              `;
            }}
          >
            {/* Brillo superior */}
            <Box
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '40%',
                background: 'linear-gradient(180deg, rgba(255, 255, 255, 0.25) 0%, transparent 100%)',
                pointerEvents: 'none',
              }}
            />
            
            <Stack gap={4} style={{ position: 'relative' }}>
              <Group gap={6} align="center">
                <IconSparkles size={16} style={{ color: '#ffffff' }} />
                <Text
                  size="xs"
                  fw={700}
                  style={{
                    color: '#ffffff',
                    fontFamily: 'Inter, sans-serif',
                    textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)',
                  }}
                >
                  ¡Mejora tu plan!
                </Text>
              </Group>
              
              <Text
                size="9px"
                style={{
                  color: 'rgba(255, 255, 255, 0.95)',
                  fontFamily: 'Inter, sans-serif',
                  lineHeight: 1.3,
                }}
              >
                Accede a todas las funciones premium
              </Text>
              
              <Group gap={4} align="center" mt={2}>
                <Text
                  size="10px"
                  fw={600}
                  style={{
                    color: '#ffffff',
                    fontFamily: 'Inter, sans-serif',
                  }}
                >
                  Ver planes
                </Text>
                <IconArrowRight size={12} style={{ color: '#ffffff' }} />
              </Group>
            </Stack>
          </Box>
        )}
      </Stack>

      {/* Bottom Section */}
      <Box style={{ flexShrink: 0, marginTop: 'auto' }}>
        <Divider
          my="md"
          color={colorScheme === 'dark' ? 'rgba(55, 65, 81, 0.3)' : 'rgba(226, 232, 240, 0.8)'}
        />
        <Stack gap="xs" style={{ alignItems: isCollapsed ? 'center' : 'stretch' }}>
          {isCollapsed ? (
            <Box style={{ display: 'flex', justifyContent: 'center' }}>
              <Menu shadow="md" width={200} position="top" withArrow zIndex={1200}>
                <Menu.Target>
                  <Tooltip label="Perfil" position="right" withArrow>
                    <UnstyledButton
                      style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '25px',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: colorScheme === 'dark'
                          ? 'rgba(30, 41, 59, 0.7)'
                          : 'rgba(255, 255, 255, 0.25)',
                        border: `1px solid ${
                          colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(255, 255, 255, 0.5)'
                        }`,
                        boxShadow: colorScheme === 'dark'
                          ? 'inset 0 1px 0 rgba(255, 255, 255, 0.1), 0 4px 16px rgba(0, 0, 0, 0.08)'
                          : '0 4px 16px rgba(0, 0, 0, 0.08), inset 0 1px 0 rgba(255, 255, 255, 0.6)',
                      }}
                    >
                    <Avatar
                      src={user.avatar ? profileService.getAvatarUrl(user.avatar) : null}
                      size={28}
                      style={{
                        backgroundColor: colorScheme === 'dark' ? '#374151' : '#e2e8f0',
                        color: colorScheme === 'dark' ? '#e2e8f0' : '#1e293b',
                        fontSize: '12px',
                        fontWeight: 600,
                      }}
                    >
                      {!user.avatar && (
                        user.nombre && user.apellidos
                          ? `${user.nombre.charAt(0)}${user.apellidos.charAt(0)}`.toUpperCase()
                          : user.username.charAt(0).toUpperCase()
                      )}
                    </Avatar>
                    </UnstyledButton>
                  </Tooltip>
                </Menu.Target>

                <Menu.Dropdown
                  style={{
                    backgroundColor: colorScheme === 'dark'
                      ? 'rgba(30, 41, 59, 0.95)'
                      : '#ffffff',
                    backdropFilter: 'blur(20px)',
                    WebkitBackdropFilter: 'blur(20px)',
                    border: `1px solid ${
                      colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(255, 255, 255, 0.3)'
                    }`,
                    borderRadius: '12px',
                    boxShadow: colorScheme === 'dark'
                      ? '0 20px 60px rgba(0, 0, 0, 0.6), inset 0 1px 0 rgba(255, 255, 255, 0.1)'
                      : '0 8px 24px rgba(0, 0, 0, 0.15), inset 0 4px 0 rgba(255, 255, 255, 0.4), 0 0 0 2px rgba(255, 255, 255, 0.2), 0 2px 8px rgba(255, 255, 255, 0.8)',
                  }}
                >
                  <Menu.Item
                    leftSection={<IconSettings size={16} />}
                    onClick={() => navigate('/settings')}
                    style={{
                      color: colorScheme === 'dark' ? '#e2e8f0' : '#1e293b',
                      fontFamily: 'Inter, sans-serif',
                    }}
                  >
                    Configuración
                  </Menu.Item>

                  {!isAdmin && (
                    <Menu.Item
                      leftSection={<IconCreditCard size={16} />}
                      onClick={() => navigate('/mejorarplan')}
                      style={{
                        color: colorScheme === 'dark' ? '#e2e8f0' : '#1e293b',
                        fontFamily: 'Inter, sans-serif',
                      }}
                    >
                      Mejorar Plan
                    </Menu.Item>
                  )}

                  <Menu.Divider style={{
                    borderColor: colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'
                  }} />

                  <Menu.Item
                    leftSection={<IconLogout size={16} />}
                    onClick={onLogout}
                    style={{
                      color: colorScheme === 'dark' ? '#ef4444' : '#dc2626',
                      fontFamily: 'Inter, sans-serif',
                    }}
                  >
                    Cerrar Sesión
                  </Menu.Item>
                </Menu.Dropdown>
              </Menu>
            </Box>
          ) : (
            <Menu shadow="md" width={200} position="top" withArrow zIndex={1200}>
              <Menu.Target>
                <UnstyledButton
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    borderRadius: '25px',
                    backgroundColor: colorScheme === 'dark'
                      ? 'rgba(30, 41, 59, 0.7)'
                      : 'rgba(255, 255, 255, 0.25)',
                    border: `1px solid ${
                      colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(255, 255, 255, 0.5)'
                    }`,
                    boxShadow: 'none',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = colorScheme === 'dark'
                      ? 'rgba(55, 65, 81, 0.4)'
                      : 'rgba(255, 255, 255, 0.6)';
                    e.currentTarget.style.transform = 'translateY(-1px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = colorScheme === 'dark'
                      ? 'rgba(30, 41, 59, 0.7)'
                      : 'rgba(255, 255, 255, 0.25)';
                    e.currentTarget.style.boxShadow = colorScheme === 'dark'
                      ? 'inset 0 1px 0 rgba(255, 255, 255, 0.1), 0 4px 16px rgba(0, 0, 0, 0.08)'
                      : '0 4px 16px rgba(0, 0, 0, 0.08), inset 0 1px 0 rgba(255, 255, 255, 0.6)';
                    e.currentTarget.style.transform = 'translateY(0px)';
                  }}
                >
                  <Group gap="xs">
                    <Avatar
                      src={user.avatar ? profileService.getAvatarUrl(user.avatar) : null}
                      size={24}
                      style={{
                        backgroundColor: colorScheme === 'dark' ? '#374151' : '#e2e8f0',
                        color: colorScheme === 'dark' ? '#e2e8f0' : '#1e293b',
                        fontSize: '10px',
                        fontWeight: 600,
                      }}
                    >
                      {!user.avatar && (
                        user.nombre && user.apellidos
                          ? `${user.nombre.charAt(0)}${user.apellidos.charAt(0)}`.toUpperCase()
                          : user.username.charAt(0).toUpperCase()
                      )}
                    </Avatar>
                    <Box style={{ flex: 1, minWidth: 0 }}>
                      <Text
                        size="xs"
                        fw={600}
                        style={{
                          color: colorScheme === 'dark' ? '#e2e8f0' : '#1e293b',
                          fontFamily: 'Inter, sans-serif',
                          lineHeight: 1.2,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {user.nombre && user.apellidos
                          ? `${user.nombre} ${user.apellidos}`
                          : user.username}
                      </Text>
                      <Text
                        size="10px"
                        style={{
                          color: colorScheme === 'dark' ? '#94a3b8' : '#64748b',
                          fontFamily: 'Inter, sans-serif',
                          lineHeight: 1.2,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {user.roles?.includes('ROLE_ADMIN') || user.roles?.includes('ADMIN')
                          ? 'Administrador'
                          : user.roles?.includes('ROLE_CREATOR') || user.roles?.includes('CREATOR')
                          ? 'Creador'
                          : 'Estudiante'}
                      </Text>
                    </Box>
                    <IconChevronDown
                      size={14}
                      style={{
                        color: colorScheme === 'dark' ? '#94a3b8' : '#64748b',
                        flexShrink: 0,
                      }}
                    />
                  </Group>
                </UnstyledButton>
              </Menu.Target>

              <Menu.Dropdown
                style={{
                  backgroundColor: colorScheme === 'dark'
                    ? 'rgba(30, 41, 59, 0.95)'
                    : '#ffffff',
                  backdropFilter: 'blur(20px)',
                  WebkitBackdropFilter: 'blur(20px)',
                  border: `1px solid ${
                    colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(255, 255, 255, 0.3)'
                  }`,
                  borderRadius: '12px',
                  boxShadow: colorScheme === 'dark'
                    ? '0 20px 60px rgba(0, 0, 0, 0.6), inset 0 1px 0 rgba(255, 255, 255, 0.1)'
                    : '0 8px 24px rgba(0, 0, 0, 0.15)',
                }}
              >
                <Menu.Item
                  leftSection={<IconSettings size={16} />}
                  onClick={() => navigate('/settings')}
                  style={{
                    color: colorScheme === 'dark' ? '#e2e8f0' : '#1e293b',
                    fontFamily: 'Inter, sans-serif',
                  }}
                >
                  Configuración
                </Menu.Item>

                {!isAdmin && (
                  <Menu.Item
                    leftSection={<IconCreditCard size={16} />}
                    onClick={() => navigate('/mejorarplan')}
                    style={{
                      color: colorScheme === 'dark' ? '#e2e8f0' : '#1e293b',
                      fontFamily: 'Inter, sans-serif',
                    }}
                  >
                    Mejorar Plan
                  </Menu.Item>
                )}

                <Menu.Divider style={{
                  borderColor: colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'
                }} />

                <Menu.Item
                  leftSection={<IconLogout size={16} />}
                  onClick={onLogout}
                  style={{
                    color: colorScheme === 'dark' ? '#ef4444' : '#dc2626',
                    fontFamily: 'Inter, sans-serif',
                  }}
                >
                  Cerrar Sesión
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
          )}
        </Stack>
      </Box>
    </Box>
  );
};

export default Sidebar;